import { z } from "zod";
import { MODEL_ROLES } from "./models.ts";

/**
 * Recommendation outcomes (openspec change `recommendation-outcomes`): how each agent
 * recommendation ended, without its content. Every field here is an ID, a date, a count or a
 * closed code; the only names are those of entities and projects the viewer may already list.
 */

/** What was recommended: a memory proposal, a wiki suggestion, an agent change item or set. */
export const RECOMMENDATION_KINDS = [
  "memory_proposal",
  "wiki_suggestion",
  "wiki_change_item",
  "wiki_change_set",
  // feedback-from-conversation: a feedback draft the agent wrote (never "Report a problem").
  "feedback_draft",
  // wiki-resolve-with-agent D9: a reply the agent drafted on a review thread.
  "wiki_thread_reply",
] as const;
export const recommendationKindSchema = z.enum(RECOMMENDATION_KINDS);
export type RecommendationKind = z.infer<typeof recommendationKindSchema>;

/** How it ended; `revised` (a wiki item the agent rewrote) may precede the end, any number of times. */
export const RECOMMENDATION_OUTCOMES = [
  "accepted",
  "accepted_after_edit",
  "dismissed",
  "ignored",
  "revised",
] as const;
export const recommendationOutcomeSchema = z.enum(RECOMMENDATION_OUTCOMES);
export type RecommendationOutcome = z.infer<typeof recommendationOutcomeSchema>;

/** The optional one-tap reasons a person gives on Dismiss or Reject. Never free text. */
export const DISMISSAL_REASONS = ["not_useful", "wrong", "duplicate"] as const;
export const dismissalReasonSchema = z.enum(DISMISSAL_REASONS);
export type DismissalReason = z.infer<typeof dismissalReasonSchema>;

/** Why a recommendation was ignored: recorded by the system, never chosen by a person. */
export const IGNORED_REASONS = ["expired", "conversation_deleted", "set_closed"] as const;
export type IgnoredReason = (typeof IGNORED_REASONS)[number];

export const RECOMMENDATION_REASONS = [...DISMISSAL_REASONS, ...IGNORED_REASONS] as const;
export const recommendationReasonSchema = z.enum(RECOMMENDATION_REASONS);
export type RecommendationReason = z.infer<typeof recommendationReasonSchema>;

/**
 * Who decided, as a role and never a person: a project role, `member` for a personal memory
 * (where no project role applies, or the decider no longer has one), or `system`.
 */
export const DECIDER_ROLES = [
  "owner",
  "manager",
  "contributor",
  "reader",
  "guest",
  "member",
  "system",
] as const;
export const deciderRoleSchema = z.enum(DECIDER_ROLES);
export type DeciderRole = z.infer<typeof deciderRoleSchema>;

/**
 * Where the recommendation came from: the post-turn pass, an agent tool, an agent change set;
 * for a feedback draft, whether the user asked for it (`requested`) or the agent suggested it
 * unprompted (`noticed`).
 */
export const RECOMMENDATION_ORIGINS = ["turn", "tool", "agent", "requested", "noticed"] as const;
export const recommendationOriginSchema = z.enum(RECOMMENDATION_ORIGINS);
export type RecommendationOrigin = z.infer<typeof recommendationOriginSchema>;

/** A producer's instructions version: `<yyyy-mm-dd>.<n>`. */
export const promptVersionSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}\.\d{1,3}$/);
/** A catalogue model key (never a profile ID, Region or ARN). */
export const recommendationModelSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9][a-z0-9._:-]*$/i);
export const recommendationModelRoleSchema = z.enum(MODEL_ROLES);

/** Below this many outcomes, a rate is not shown (`null`): it would mislead. */
export const RECOMMENDATION_RATE_MIN_OUTCOMES = 10;
/** Raw outcome rows are kept at most this long after the decision; daily counts 13 months. */
export const RECOMMENDATION_OUTCOME_RETENTION_DAYS = 90;

/** Optional body of a Dismiss or Reject: one reason code, or nothing. */
export const dismissalRequestSchema = z
  .object({ reason: dismissalReasonSchema.optional() })
  .strict();
export type DismissalRequest = z.infer<typeof dismissalRequestSchema>;

// ── Report ────────────────────────────────────────────────────────────────────────────────

const daySchema = z.iso.date();
const uuid = z.uuid();
const count = z.int().min(0);

/** One set of outcome counts; `acceptanceRate` is null under ten outcomes. */
const recommendationCountsSchema = z
  .object({
    /** Every ended recommendation: accepted, accepted after edit, dismissed and ignored. */
    shown: count,
    accepted: count,
    acceptedAfterEdit: count,
    dismissed: count,
    ignored: count,
    /** Agent rewrites of a wiki item before it ended (not an outcome of its own). */
    revised: count,
    acceptanceRate: z.number().min(0).max(1).nullable(),
    /**
     * Dismissed over shown, null under ten outcomes (feedback-from-conversation: how tuning reads
     * a trigger's false positives). Absent from older APIs.
     */
    dismissalRate: z.number().min(0).max(1).nullable().optional(),
  })
  .strict();
export type RecommendationCounts = z.infer<typeof recommendationCountsSchema>;

const reasonCountSchema = z.object({ reason: recommendationReasonSchema, count }).strict();

/** One producer: model role, model and prompt version; null when unknown (older rows). */
const recommendationVersionSchema = recommendationCountsSchema
  .extend({
    modelRole: recommendationModelRoleSchema.nullable(),
    model: recommendationModelSchema.nullable(),
    promptVersion: promptVersionSchema.nullable(),
    /**
     * For a feedback draft: asked for (`requested`) or unprompted (`noticed`), each version split
     * by it (feedback-from-conversation). Absent for other kinds and from older APIs.
     */
    origin: recommendationOriginSchema.optional(),
  })
  .strict();

const recommendationKindFiguresSchema = recommendationCountsSchema
  .extend({
    kind: recommendationKindSchema,
    /** Dismissal and ignore reasons, most frequent first. */
    reasons: z.array(reasonCountSchema),
    versions: z.array(recommendationVersionSchema),
  })
  .strict();
export type RecommendationKindFigures = z.infer<typeof recommendationKindFiguresSchema>;

export const recommendationReportSchema = z
  .object({
    period: z.object({ from: daySchema, to: daySchema }).strict(),
    freshness: z.object({ computedAt: z.iso.datetime().nullable(), stale: z.boolean() }).strict(),
    entityId: uuid.nullable(),
    kinds: z.array(recommendationKindFiguresSchema),
    /**
     * Per project of the scope (personal projects together, with null refs), per kind. Personal
     * memory proposals appear on a project only when it had five active users in the period.
     */
    projects: z.array(
      z
        .object({
          projectId: uuid.nullable(),
          name: z.string().nullable(),
          entityId: uuid.nullable(),
          kinds: z.array(recommendationCountsSchema.extend({ kind: recommendationKindSchema })),
        })
        .strict(),
    ),
    /** The caller may download the export (tenant admins). */
    canExport: z.boolean(),
  })
  .strict();
export type RecommendationReport = z.infer<typeof recommendationReportSchema>;

// ── Export ────────────────────────────────────────────────────────────────────────────────

/** One daily count of the export: the report's dimensions, never a subject or conversation. */
export const recommendationExportRowSchema = z
  .object({
    day: daySchema,
    entityId: uuid.nullable(),
    entityName: z.string().nullable(),
    projectId: uuid.nullable(),
    projectName: z.string().nullable(),
    kind: recommendationKindSchema,
    origin: recommendationOriginSchema,
    modelRole: recommendationModelRoleSchema.nullable(),
    model: recommendationModelSchema.nullable(),
    promptVersion: promptVersionSchema.nullable(),
    outcome: recommendationOutcomeSchema,
    reason: recommendationReasonSchema.nullable(),
    count: z.int().min(1),
  })
  .strict();
export type RecommendationExportRow = z.infer<typeof recommendationExportRowSchema>;

/** The tuning export (`schemaVersion: 1`): what the offline reader reads. */
export const recommendationOutcomeExportSchema = z
  .object({
    schemaVersion: z.literal(1),
    generatedAt: z.iso.datetime(),
    period: z.object({ from: daySchema, to: daySchema }).strict(),
    entityId: uuid.nullable(),
    rows: z.array(recommendationExportRowSchema),
  })
  .strict();
export type RecommendationOutcomeExport = z.infer<typeof recommendationOutcomeExportSchema>;

export const recommendationReportQuerySchema = z
  .object({
    from: daySchema.optional(),
    to: daySchema.optional(),
    entity: uuid.optional(),
    format: z.literal("export").optional(),
  })
  .strict();
export type RecommendationReportQuery = z.infer<typeof recommendationReportQuerySchema>;

const TERMINAL: readonly RecommendationOutcome[] = [
  "accepted",
  "accepted_after_edit",
  "dismissed",
  "ignored",
];

/** Counts of outcome rows: `shown` is every terminal outcome; the rate needs ten of them. */
export function recommendationCounts(
  rows: readonly { outcome: RecommendationOutcome; count: number }[],
): RecommendationCounts {
  const of = (o: RecommendationOutcome) =>
    rows.reduce((n, r) => n + (r.outcome === o ? r.count : 0), 0);
  const shown = TERMINAL.reduce((n, o) => n + of(o), 0);
  const accepted = of("accepted");
  const acceptedAfterEdit = of("accepted_after_edit");
  return {
    shown,
    accepted,
    acceptedAfterEdit,
    dismissed: of("dismissed"),
    ignored: of("ignored"),
    revised: of("revised"),
    acceptanceRate:
      shown >= RECOMMENDATION_RATE_MIN_OUTCOMES ? (accepted + acceptedAfterEdit) / shown : null,
    dismissalRate: shown >= RECOMMENDATION_RATE_MIN_OUTCOMES ? of("dismissed") / shown : null,
  };
}
